import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { Box, Button, Card, CardContent, TextField, Typography, Paper, FormControl, InputLabel, Select, MenuItem, Grid, Chip, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

import SecurityIcon from "@material-ui/icons/Security";
import PageTitle from "../../components/PageTitle/PageTitle";
import SubscriptionService from "./Locality/Service/subscriptionService";
import AcademicYearService from "./Locality/Service/academicYearService";
import { escapeHtml, openPrintHtml } from "../../utils/printHtml";
/** HashRouter: query string lives on the route (location.search), not always window.location.search */
function getHashQueryParams() {
  const fromRoute = typeof window !== "undefined" ? window.location.hash || "" : "";
  const q = fromRoute.indexOf("?");
  if (q === -1) return new URLSearchParams();
  return new URLSearchParams(fromRoute.slice(q + 1));
}

function getOrderIdFromLocation(routeSearch) {
  const fromRoute = new URLSearchParams(routeSearch || "").get("orderId");
  if (fromRoute) return fromRoute;
  const fromWindow = new URLSearchParams(window.location.search || "").get("orderId");
  if (fromWindow) return fromWindow;
  return getHashQueryParams().get("orderId") || "";
}

function getPaymentStatusFromLocation(routeSearch) {
  const fromRoute = new URLSearchParams(routeSearch || "").get("paymentStatus");
  if (fromRoute) return String(fromRoute).toLowerCase();
  const fromWindow = new URLSearchParams(window.location.search || "").get("paymentStatus");
  if (fromWindow) return String(fromWindow).toLowerCase();
  return String(getHashQueryParams().get("paymentStatus") || "").toLowerCase();
}

function clearPaymentReturnQuery(history) {
  if (!history?.replace) return;
  history.replace("/app/upgrade-plan");
}

function redirectToUpgradePlanHome() {
  const base = window.location.origin || "https://www.montestory.in";
  window.location.href = `${base}/#/app/upgrade-plan`;
}

const palette = {
  paper: "#ffffff",
  border: "#e8eef4",
  title: "#0f3b62",
  label: "#64748b",
  value: "#0f2744",
  divider: "#eef2f6",
};

/** e.g. "2026-27" → "2026-2027" for display */
function formatAcademicYearDisplay(ay) {
  if (ay == null || ay === "") return "—";
  const s = String(ay).trim();
  if (/^\d{4}-\d{4}$/.test(s)) return s;
  const m = s.match(/^(\d{4})-(\d{2})$/);
  if (m) return `${m[1]}-20${m[2]}`;
  return s;
}

function formatDateOnly(d) {
  if (!d) return "—";
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "—";
    return dt.toLocaleDateString();
  } catch {
    return "—";
  }
}

function getSocietyName(schoolRef) {
  return String(schoolRef?.societyName || "").trim();
}

function renderSocietyInvoiceHtml(schoolRef) {
  const societyName = getSocietyName(schoolRef);
  if (!societyName) return "";
  return `
            <div class="cell">
              <div class="label">Society Name</div>
              <div class="val">${escapeHtml(societyName)}</div>
            </div>`;
}

function InvoiceField({ label, value }) {
  return (
    <Box px={0.5} py={1.75} minHeight={72}>
      <Typography variant="caption" style={{ color: palette.label, fontWeight: 600, display: "block" }}>
        {label}
      </Typography>
      <Typography
        variant="body1"
        style={{ color: palette.value, fontWeight: 800, marginTop: 6, wordBreak: "break-word" }}
      >
        {value ?? "—"}
      </Typography>
    </Box>
  );
}

function InvoiceDetailGrid({ inv }) {
  const societyName = getSocietyName(inv?.schooleId);
  const amountStr = inv?.amount != null ? `₹${Number(inv.amount).toLocaleString()}` : "—";
  const studentsStr = inv?.studentCount != null ? String(inv.studentCount) : "—";

  const renderRow = (cells, key) => (
    <Box key={key}>
      <Grid container spacing={0}>
        {cells.map((cell, idx) => (
          <Grid item xs={12} sm={4} key={`${key}-${idx}`}>
            {cell.label ? <InvoiceField label={cell.label} value={cell.value} /> : <Box minHeight={72} />}
          </Grid>
        ))}
      </Grid>
      <Box style={{ height: 1, background: palette.divider }} />
    </Box>
  );

  return (
    <Box>
      {renderRow(
        [
          { label: "School", value: inv?.schooleId?.schooleName || "—" },
          { label: "Order ID", value: inv?.orderId || "—" },
          { label: "Receipt No.", value: inv?.receiptNumber || "—" },
        ],
        "row-1",
      )}
      {renderRow(
        societyName
          ? [
              { label: "Society Name", value: societyName },
              { label: "Students", value: studentsStr },
              { label: "Amount", value: amountStr },
            ]
          : [
              { label: "Students", value: studentsStr },
              { label: "Amount", value: amountStr },
              { label: "", value: "" },
            ],
        "row-2",
      )}
      {renderRow(
        [
          { label: "Academic Year", value: formatAcademicYearDisplay(inv?.academicYear) },
          { label: "", value: "" },
          { label: "", value: "" },
        ],
        "row-3",
      )}
      <Grid container spacing={0}>
        <Grid item xs={12} sm={4}>
          <InvoiceField label="Date" value={formatDateOnly(inv?.paidAt)} />
        </Grid>
      </Grid>
    </Box>
  );
}

function InvoiceCardHeader({ onDownload }) {
  return (
    <Box
      display="grid"
      style={{
        gridTemplateColumns: "minmax(260px, 1fr) auto minmax(220px, 1fr)",
        alignItems: "center",
        gap: 12,
      }}
    >
      <Box display="flex" flexDirection="column" style={{ lineHeight: 1.15 }}>
        <Typography style={{ fontWeight: 900, fontSize: "1.15rem", color: palette.title }}>Montestory.in</Typography>
        <Typography style={{ fontWeight: 800, fontSize: "0.9rem", color: "#334155" }}>
          (Srshta Tech Solutions Pvt Ltd)
        </Typography>
      </Box>

      <Box textAlign="center">
        <Typography style={{ fontWeight: 900, fontSize: "1.75rem", color: palette.title, letterSpacing: 0.2 }}>
          Invoice
        </Typography>
        <Box display="flex" justifyContent="center" alignItems="center" style={{ marginTop: 6, gap: 10 }}>
          <Box style={{ width: 74, height: 2, background: "#cbd5e1", borderRadius: 2 }} />
          <Box display="flex" style={{ gap: 6 }}>
            <Box style={{ width: 6, height: 6, borderRadius: 6, background: "#2563eb" }} />
            <Box style={{ width: 6, height: 6, borderRadius: 6, background: "#2563eb" }} />
            <Box style={{ width: 6, height: 6, borderRadius: 6, background: "#2563eb" }} />
          </Box>
          <Box style={{ width: 74, height: 2, background: "#cbd5e1", borderRadius: 2 }} />
        </Box>
      </Box>

      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="outlined"
          onClick={onDownload}
          style={{
            textTransform: "none",
            fontWeight: 800,
            borderColor: "#93c5fd",
            color: "#1d4ed8",
            background: "#ffffff",
          }}
        >
          Download Invoice
        </Button>
      </Box>
    </Box>
  );
}

function InvoiceCard({ inv, onDownload }) {
  return (
    <Card
      style={{
        maxWidth: 900,
        marginTop: 24,
        borderRadius: 16,
        boxShadow: "0 4px 24px rgba(15, 59, 98, 0.08)",
        border: `1px solid ${palette.border}`,
        overflow: "hidden",
        background: palette.paper,
      }}
    >
      <CardContent style={{ padding: "24px 28px 20px" }}>
        <InvoiceCardHeader onDownload={() => onDownload(inv)} />
        <Box style={{ height: 1, background: palette.divider, margin: "18px 0 8px" }} />
        <InvoiceDetailGrid inv={inv} />
      </CardContent>
    </Card>
  );
}

function ComputerGeneratedFooter() {
  return (
    <Box mt={3} textAlign="center">
      <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 8, marginBottom: 8 }}>
        <SecurityIcon style={{ fontSize: 18, color: "#2563eb" }} />
        <Typography variant="caption" style={{ color: "#475569", fontWeight: 600 }}>
          This is a computer generated receipt and invoice. No signature required.
        </Typography>
      </Box>
      <Box
        style={{
          height: 1,
          background: "linear-gradient(90deg, transparent, #cbd5e1 20%, #cbd5e1 80%, transparent)",
          maxWidth: 400,
          margin: "0 auto",
          position: "relative",
        }}
      >
        <Box
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%) rotate(45deg)",
            width: 8,
            height: 8,
            background: "#94a3b8",
          }}
        />
      </Box>
    </Box>
  );
}

export default function UpgradePlan() {
  const routerLocation = useLocation();
  const history = useHistory();
  const userDetails = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userDetail") || "{}");
    } catch {
      return {};
    }
  }, []);

  const [academicYearId, setAcademicYearId] = useState("");
  const [academicYearOptions, setAcademicYearOptions] = useState([]); // [{ id, label }]
  const [studentCount, setStudentCount] = useState("");
  const [amount, setAmount] = useState("");
  const [billingType, setBillingType] = useState("PER_STUDENT");
  const [pricePerStudent, setPricePerStudent] = useState(null);
  const [fixedAmount, setFixedAmount] = useState(null);
  const [feeSource, setFeeSource] = useState("");
  const [loading, setLoading] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [invoiceList, setInvoiceList] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [error, setError] = useState("");
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState({ open: false, type: null, payment: null });

  const isSchool = String(userDetails?.role || "").toUpperCase() === "SCHOOLE";

  const paymentOrderId = useMemo(
    () => getOrderIdFromLocation(routerLocation.search),
    [routerLocation.search, routerLocation.pathname, routerLocation.hash],
  );

  const paymentReturnStatus = useMemo(
    () => getPaymentStatusFromLocation(routerLocation.search),
    [routerLocation.search, routerLocation.pathname, routerLocation.hash],
  );

  const openPaymentDialog = (type, payment = null) => {
    setPaymentDialog({ open: true, type, payment });
  };

  const closePaymentDialog = () => {
    setPaymentDialog({ open: false, type: null, payment: null });
    clearPaymentReturnQuery(history);
  };

  const handlePaymentSuccessOk = () => {
    closePaymentDialog();
    redirectToUpgradePlanHome();
  };

  const isPaymentReturn = Boolean(paymentOrderId);
  const isPaymentFailedReturn =
    paymentReturnStatus === "failed" ||
    (receipt && ["FAILED", "ABORTED"].includes(String(receipt.status || "").toUpperCase()));
  const hideUpgradeForm = isPaymentReturn && !isPaymentFailedReturn;

  // Show success/failure popup from CCAvenue return (paymentStatus or orderId in URL).
  useEffect(() => {
    if (!isSchool || !paymentOrderId) return;
    const notifiedKey = `paymentDialogShown_${paymentOrderId}`;
    if (sessionStorage.getItem(notifiedKey)) return;

    if (paymentReturnStatus === "success") {
      sessionStorage.setItem(notifiedKey, "1");
      openPaymentDialog("success", { orderId: paymentOrderId });
    } else if (paymentReturnStatus === "failed") {
      sessionStorage.setItem(notifiedKey, "1");
      openPaymentDialog("failure", { orderId: paymentOrderId, status: "FAILED" });
    }
  }, [isSchool, paymentOrderId, paymentReturnStatus]);

  useEffect(() => {
    if (!isSchool) return;
    const schooleId = userDetails?.schooleId?._id || userDetails?.schooleId || userDetails?.schoolId;
    if (!schooleId) return;
    let alive = true;
    AcademicYearService.getAllAcademicYear(schooleId)
      .then((res) => {
        if (!alive) return;
        const list = Array.isArray(res) ? res : [];
        const opts = list
          .filter((x) => String(x?.status || "Active") === "Active")
          .map((x) => ({
            id: String(x?._id || ""),
            label: String(x?.acadamicYear || "").trim(),
          }))
          .filter((x) => x.id && x.label);
        opts.sort((a, b) => {
          const ay = parseInt(String(a.label).split("-")[0], 10) || 0;
          const by = parseInt(String(b.label).split("-")[0], 10) || 0;
          return by - ay;
        });
        setAcademicYearOptions(opts);
        if (opts.length > 0 && !opts.some((x) => x.id === String(academicYearId || ""))) {
          setAcademicYearId(opts[0].id);
        }
      })
      .catch(() => {
        if (!alive) return;
        setAcademicYearOptions([]);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSchool]);

  const isFixedAmountBilling = String(billingType || "PER_STUDENT").toUpperCase() === "FIXED_AMOUNT";

  const fetchQuote = async () => {
    setError("");
    try {
      if (!academicYearId) return;
      const res = await SubscriptionService.getUpgradeQuote(academicYearId);
      const data = res?.data || res;
      const sc = data?.studentCount ?? "";
      const nextBillingType = String(data?.billingType || "PER_STUDENT").toUpperCase();
      const pps = data?.pricePerStudent ?? null;
      const fixed = data?.fixedAmount ?? null;
      setStudentCount(String(sc));
      setBillingType(nextBillingType);
      setPricePerStudent(pps != null ? Number(pps) : null);
      setFixedAmount(fixed != null ? Number(fixed) : null);
      setFeeSource(String(data?.feeSource || ""));
      setAmount(String(data?.amount ?? ""));
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load upgrade quote.");
    }
  };

  useEffect(() => {
    if (!isSchool) return;
    fetchQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSchool, academicYearId]);

  useEffect(() => {
    if (!isSchool) return;
    if (!paymentOrderId) return;
    let alive = true;
    setReceiptLoading(true);
    SubscriptionService.getReceipt(paymentOrderId)
      .then((res) => {
        if (!alive) return;
        const data = res?.data || res;
        const payment = data?.payment || null;
        setReceipt(payment);

        if (payment) {
          setPaymentDialog((prev) => {
            if (!prev.open) return prev;
            return { ...prev, payment: { ...prev.payment, ...payment } };
          });
        }

        // Fallback when URL has orderId only (e.g. ?orderId=SUB-...)
        const notifiedKey = `paymentDialogShown_${paymentOrderId}`;
        if (sessionStorage.getItem(notifiedKey)) return;

        const status = String(payment?.status || "").toUpperCase();
        if (status === "SUCCESS") {
          sessionStorage.setItem(notifiedKey, "1");
          openPaymentDialog("success", payment || { orderId: paymentOrderId });
        } else if (status === "FAILED" || status === "ABORTED") {
          sessionStorage.setItem(notifiedKey, "1");
          openPaymentDialog("failure", payment || { orderId: paymentOrderId, status });
        }
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setReceiptLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [isSchool, paymentOrderId]);

  // After successful payment, load invoice so both cards match the designed layout.
  useEffect(() => {
    if (!isSchool || !receipt || String(receipt.status || "").toUpperCase() !== "SUCCESS") return;
    if (!paymentOrderId) return;
    let alive = true;
    SubscriptionService.getInvoice(paymentOrderId)
      .then((res) => {
        if (!alive) return;
        const data = res?.data || res;
        setInvoice(data?.invoice || null);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [isSchool, receipt, paymentOrderId]);

  // Default behavior: load all successful invoices for the school
  useEffect(() => {
    if (!isSchool) return;
    let alive = true;
    SubscriptionService.listSuccessfulInvoices(50)
      .then((res) => {
        if (!alive) return;
        const data = res?.data || res;
        const rows = Array.isArray(data?.rows) ? data.rows : [];
        setInvoiceList(rows);
      })
      .catch(() => {
        if (!alive) return;
        setInvoiceList([]);
      });
    return () => {
      alive = false;
    };
  }, [isSchool, paymentOrderId]);

  const handleInvoice = async () => {
    setError("");
    setInvoice(null);
    const orderId = paymentOrderId || getOrderIdFromLocation(routerLocation.search);
    if (!orderId) {
      setError("OrderId missing. Please complete payment first.");
      return;
    }
    setInvoiceLoading(true);
    try {
      const res = await SubscriptionService.getInvoice(orderId);
      const data = res?.data || res;
      setInvoice(data?.invoice || null);
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load invoice.");
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleDownloadInvoice = (inv) => {
    const invoiceToUse = inv || invoice;
    if (!invoiceToUse) {
      setError("Invoice not loaded yet.");
      return;
    }

    // Browser-native download path: open print view → user can "Save as PDF".
    const title = `Invoice - ${invoiceToUse?.orderId || "order"}`;
    const schoolName = escapeHtml(invoiceToUse?.schooleId?.schooleName || "—");
    const orderId = escapeHtml(invoiceToUse?.orderId || "—");
    const receiptNumber = escapeHtml(invoiceToUse?.receiptNumber || "—");
    const studentCount =
      invoiceToUse?.studentCount != null ? escapeHtml(invoiceToUse.studentCount) : "—";
    const amount =
      invoiceToUse?.amount != null
        ? escapeHtml(`₹${Number(invoiceToUse.amount).toLocaleString()}`)
        : "—";
    const academicYear = escapeHtml(formatAcademicYearDisplay(invoiceToUse?.academicYear) || "—");
    const paidAt = escapeHtml(formatDateOnly(invoiceToUse?.paidAt) || "—");
    const html = `
<!doctype html>
<html>
  <head>
    <meta charset="utf-8"/>
    <meta name="viewport" content="width=device-width, initial-scale=1"/>
    <title>${escapeHtml(title)}</title>
    <style>
      * { box-sizing: border-box; }
      body { margin: 0; padding: 24px; font-family: Arial, Helvetica, sans-serif; background: #ffffff; color: #0f2744; }
      .wrap { max-width: 900px; margin: 0 auto; }
      .card { border: 1px solid #e8eef4; border-radius: 16px; padding: 22px 26px; }
      .h { display:flex; align-items:center; justify-content:space-between; gap: 12px; }
      .t { font-size: 20px; font-weight: 900; color: #0f3b62; margin: 0; }
      .brand { margin: 0; line-height: 1.2; }
      .brand1 { font-size: 18px; font-weight: 900; color: #0f3b62; }
      .brand2 { font-size: 13px; font-weight: 800; color: #334155; margin-top: 2px; }
      .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0; margin-top: 12px; }
      .row { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); border-bottom: 1px solid #eef2f6; }
      .cell { padding: 12px 8px; min-height: 72px; }
      .label { font-size: 11px; color: #64748b; font-weight: 700; }
      .val { margin-top: 6px; font-size: 15px; font-weight: 800; word-break: break-word; }
      .foot { margin-top: 18px; font-size: 12px; color: #475569; text-align: center; font-weight: 600; }
      @media print {
        body { padding: 0; }
        .card { border: none; border-radius: 0; padding: 0; }
      }
      @media (max-width: 720px) {
        .grid { grid-template-columns: 1fr; }
      }
    </style>
  </head>
  <body>
    <div class="wrap">
      <div class="card">
        <div class="h">
          <div>
            <p class="brand brand1">Montestory.in</p>
            <p class="brand brand2">(Srshta Tech Solutions Pvt Ltd)</p>
          </div>
          <h1 class="t">Invoice</h1>
        </div>
        <div class="grid">
          <div class="row">
            <div class="cell">
              <div class="label">School</div>
              <div class="val">${schoolName}</div>
            </div>
            <div class="cell">
              <div class="label">Order ID</div>
              <div class="val">${orderId}</div>
            </div>
            <div class="cell">
              <div class="label">Receipt No.</div>
              <div class="val">${receiptNumber}</div>
            </div>
          </div>
          <div class="row">
            ${renderSocietyInvoiceHtml(invoiceToUse?.schooleId)}
            <div class="cell">
              <div class="label">Students</div>
              <div class="val">${studentCount}</div>
            </div>
            <div class="cell">
              <div class="label">Amount</div>
              <div class="val">${amount}</div>
            </div>
          </div>
          <div class="row">
            <div class="cell">
              <div class="label">Academic Year</div>
              <div class="val">${academicYear}</div>
            </div>
            <div class="cell"></div>
            <div class="cell"></div>
          </div>
          <div class="row" style="border-bottom:none;">
            <div class="cell">
              <div class="label">Date</div>
              <div class="val">${paidAt}</div>
            </div>
            <div class="cell"></div>
            <div class="cell"></div>
          </div>
        </div>
        <div class="foot">This is a computer generated receipt and invoice. No signature required.</div>
      </div>
      <script>window.onload = () => { window.focus(); window.print(); };</script>
    </div>
  </body>
</html>`;

    const w = openPrintHtml(html);
    if (!w) {
      setError("Popup blocked. Please allow popups to download invoice.");
    }
  };

  const handlePay = async () => {
    setLoading(true);
    setError("");
    try {
      const sc = Number(studentCount || 0);
      const res = await SubscriptionService.initiateUpgradePayment({
        academicYearId,
        studentCount: Number.isFinite(sc) && sc > 0 ? sc : undefined,
      });
      const data = res?.data || res;
      const ccavUrl =
        process.env.REACT_APP_CCAV_URL || "https://test.ccavenue.com/transaction/transaction.do?command=initiateTransaction";
      if (!data?.encRequest || !data?.access_code) {
        throw new Error("Missing CCAvenue params from server (encRequest/access_code).");
      }

      const form = document.createElement("form");
      form.method = "POST";
      form.action = ccavUrl;

      const addHidden = (name, value) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = value;
        form.appendChild(input);
      };

      addHidden("encRequest", data.encRequest);
      addHidden("access_code", data.access_code);

      document.body.appendChild(form);
      form.submit();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Payment initiation failed.");
      setLoading(false);
    }
  };

  const receiptSuccess = receipt && String(receipt.status || "").toUpperCase() === "SUCCESS";

  if (!isSchool) {
    return (
      <Box p={3}>
        <Typography>You don&apos;t have access to this page.</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <PageTitle title="Upgrade to Paid plan" />

      {!hideUpgradeForm ? (
      <Card style={{ maxWidth: 900, marginTop: 16, borderRadius: 16, boxShadow: "0 4px 24px rgba(15, 59, 98, 0.08)" }}>
        <CardContent>
          <Box display="flex" flexDirection="column" style={{ gap: 14 }}>
            {academicYearOptions.length > 0 ? (
              <FormControl variant="outlined">
                <InputLabel id="upgrade-academic-year-label">Academic year</InputLabel>
                <Select
                  labelId="upgrade-academic-year-label"
                  value={academicYearId}
                  onChange={(e) => setAcademicYearId(String(e.target.value))}
                  label="Academic year"
                >
                  {academicYearOptions.map((y) => (
                    <MenuItem key={y.id} value={y.id}>
                      {y.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                label="Academic year"
                value={academicYearId}
                onChange={(e) => setAcademicYearId(e.target.value)}
                variant="outlined"
                placeholder="2026-27"
              />
            )}

            {isFixedAmountBilling ? (
              <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                <Typography variant="body2" style={{ color: "#374151", fontWeight: 600 }}>
                  Fixed upgrade amount: ₹{Number(fixedAmount || amount || 0).toLocaleString()}
                </Typography>
                <Chip
                  size="small"
                  label="Fixed amount billing"
                  style={{
                    fontWeight: 600,
                    backgroundColor: "#ede9fe",
                    color: "#5b21b6",
                  }}
                />
                <Chip
                  size="small"
                  label={
                    feeSource === "school"
                      ? "School-specific fee"
                      : feeSource === "global"
                        ? "Default fee (all schools)"
                        : "System default (₹500)"
                  }
                  style={{
                    fontWeight: 600,
                    backgroundColor:
                      feeSource === "school" ? "#fef3c7" : feeSource === "global" ? "#dbeafe" : "#f3f4f6",
                    color: feeSource === "school" ? "#92400e" : feeSource === "global" ? "#1e40af" : "#4b5563",
                  }}
                />
              </Box>
            ) : pricePerStudent != null ? (
              <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                <Typography variant="body2" style={{ color: "#374151", fontWeight: 600 }}>
                  Fee per student: ₹{Number(pricePerStudent).toLocaleString()}
                </Typography>
                <Chip
                  size="small"
                  label="Per student billing"
                  style={{
                    fontWeight: 600,
                    backgroundColor: "#ecfdf5",
                    color: "#047857",
                  }}
                />
                <Chip
                  size="small"
                  label={
                    feeSource === "school"
                      ? "School-specific fee"
                      : feeSource === "global"
                        ? "Default fee (all schools)"
                        : "System default (₹500)"
                  }
                  style={{
                    fontWeight: 600,
                    backgroundColor:
                      feeSource === "school" ? "#fef3c7" : feeSource === "global" ? "#dbeafe" : "#f3f4f6",
                    color: feeSource === "school" ? "#92400e" : feeSource === "global" ? "#1e40af" : "#4b5563",
                  }}
                />
              </Box>
            ) : null}

            <TextField
              label="Number of students"
              value={studentCount}
              variant="outlined"
              onChange={(e) => {
                const raw = e?.target?.value ?? "";
                const digits = String(raw).replace(/[^\d]/g, "");
                setStudentCount(digits);

                if (isFixedAmountBilling) return;

                const n = Number(digits || 0);
                const pps = Number(pricePerStudent);
                if (Number.isFinite(n) && n > 0 && Number.isFinite(pps) && pps > 0) {
                  setAmount(String(n * pps));
                }
              }}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              helperText={
                isFixedAmountBilling
                  ? "Student count is shown for reference; payment uses the fixed amount below."
                  : "Amount is calculated as students × fee per student."
              }
            />

            <TextField
              label="Amount to be paid"
              value={amount ? `₹${Number(amount).toLocaleString()}` : ""}
              variant="outlined"
              InputProps={{ readOnly: true }}
            />

            {error ? (
              <Paper style={{ padding: 10, background: "#fff7ed", border: "1px solid #fed7aa" }}>
                <Typography style={{ color: "#9a3412", fontWeight: 700 }}>{error}</Typography>
              </Paper>
            ) : null}

            <Box display="flex" alignItems="center" style={{ gap: 12 }} flexWrap="wrap">
              <Button variant="outlined" onClick={fetchQuote} disabled={loading} style={{ textTransform: "none" }}>
                Refresh amount
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handlePay}
                disabled={loading || !amount || Number(amount) <= 0}
                style={{ textTransform: "none", fontWeight: 800 }}
              >
                Click here to pay online
              </Button>

              {receiptSuccess ? (
                <Button
                  variant="contained"
                  onClick={handleInvoice}
                  disabled={invoiceLoading}
                  style={{ textTransform: "none", fontWeight: 800, background: "#111827", color: "white" }}
                >
                  {invoiceLoading ? "Loading invoice..." : "Refresh invoice"}
                </Button>
              ) : null}
            </Box>

          </Box>
        </CardContent>
      </Card>
      ) : null}

      {hideUpgradeForm && receiptLoading && !paymentDialog.open ? (
        <Paper style={{ padding: 24, marginTop: 16, maxWidth: 900, textAlign: "center" }}>
          <CircularProgress size={28} style={{ marginBottom: 12 }} />
          <Typography style={{ color: palette.label, fontWeight: 600 }}>
            Confirming your payment…
          </Typography>
        </Paper>
      ) : null}

      {/* If orderId is present in URL, show that invoice. Otherwise show all successful invoices. */}
      {!paymentOrderId && Array.isArray(invoiceList) && invoiceList.length > 0
        ? invoiceList.map((inv) => <InvoiceCard key={inv.orderId} inv={inv} onDownload={handleDownloadInvoice} />)
        : null}

      {paymentOrderId && invoice ? (
        <InvoiceCard inv={invoice} onDownload={handleDownloadInvoice} />
      ) : null}

      {paymentOrderId && receiptLoading && !invoice ? (
        <Paper style={{ padding: 16, marginTop: 16, maxWidth: 900 }}>
          <Typography style={{ color: palette.label, fontWeight: 600 }}>
            Loading payment details…
          </Typography>
        </Paper>
      ) : null}

      {invoice ? <ComputerGeneratedFooter /> : null}

      {/* Payment success / failure popup after CCAvenue return */}
      <Dialog
        open={paymentDialog.open && paymentDialog.type === "success"}
        onClose={closePaymentDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle style={{ textAlign: "center", paddingBottom: 8 }}>
          <CheckCircleIcon style={{ fontSize: 56, color: "#16a34a", display: "block", margin: "0 auto 8px" }} />
          <Typography style={{ fontWeight: 900, fontSize: "1.25rem", color: "#0f3b62" }}>
            Payment Successful
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography style={{ color: "#334155", marginBottom: 12, textAlign: "center" }}>
            Your subscription payment was completed successfully.
          </Typography>
          {receiptLoading && !paymentDialog.payment?.receiptNumber ? (
            <Typography variant="body2" style={{ color: "#64748b", textAlign: "center" }}>
              Loading receipt details…
            </Typography>
          ) : (
            <Box style={{ background: "#f0fdf4", borderRadius: 10, padding: 14, border: "1px solid #bbf7d0" }}>
              <Typography variant="body2" style={{ color: "#334155" }}>
                <strong>Order ID:</strong> {paymentDialog.payment?.orderId || paymentOrderId || "—"}
              </Typography>
              <Typography variant="body2" style={{ color: "#334155", marginTop: 6 }}>
                <strong>Receipt No.:</strong> {paymentDialog.payment?.receiptNumber || "—"}
              </Typography>
              <Typography variant="body2" style={{ color: "#334155", marginTop: 6 }}>
                <strong>Amount:</strong>{" "}
                {paymentDialog.payment?.amount != null
                  ? `₹${Number(paymentDialog.payment.amount).toLocaleString()}`
                  : "—"}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <Button
            color="primary"
            variant="contained"
            fullWidth
            onClick={handlePaymentSuccessOk}
            style={{ textTransform: "none", fontWeight: 800 }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={paymentDialog.open && paymentDialog.type === "failure"}
        onClose={closePaymentDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle style={{ textAlign: "center", paddingBottom: 8 }}>
          <ErrorOutlineIcon style={{ fontSize: 56, color: "#dc2626", display: "block", margin: "0 auto 8px" }} />
          <Typography style={{ fontWeight: 900, fontSize: "1.25rem", color: "#0f3b62" }}>
            Payment Failed
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography style={{ color: "#334155", marginBottom: 12, textAlign: "center" }}>
            Your payment could not be completed. Please try again or contact support if amount was debited.
          </Typography>
          <Box style={{ background: "#fef2f2", borderRadius: 10, padding: 14, border: "1px solid #fecaca" }}>
            <Typography variant="body2" style={{ color: "#334155" }}>
              <strong>Order ID:</strong> {paymentDialog.payment?.orderId || paymentOrderId || "—"}
            </Typography>
            <Typography variant="body2" style={{ color: "#334155", marginTop: 6 }}>
              <strong>Status:</strong> {String(paymentDialog.payment?.status || "FAILED").toUpperCase()}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px", gap: 8 }}>
          <Button onClick={closePaymentDialog} style={{ textTransform: "none", fontWeight: 700 }}>
            Close
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              closePaymentDialog();
              handlePay();
            }}
            disabled={loading}
            style={{ textTransform: "none", fontWeight: 800 }}
          >
            Try Again
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
